import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Plus, Trash2, Instagram, Phone, Cake, Ticket, Edit2,
  MessageCircle, Bell, Heart, Send, X, Copy, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import {
  getClient, listTattoos, createTattoo, deleteTattoo, updateTattoo,
  listLuckyNumbers, createLuckyNumber, deleteLuckyNumber, nextLuckyNumber,
  deleteClient, updateClient,
  listReminders, createReminder, updateReminder, deleteReminder,
} from "@/lib/api";
import {
  formatBRL, formatDate, todayISO, paymentLabel, statusLabel, statusBadge, fileToBase64,
  waLink, igDMLink, composeAppointmentMessage, composeCareMessage, composeGeneralMessage, balanceOfTattoo, photosOf,
} from "@/lib/format";
import { getSettings, carePdfPublicUrl } from "@/lib/api";
import { useMessageTemplates } from "@/lib/useTemplates";
import { confirmAction } from "@/lib/confirm";

const tattooBlank = {
  description: "", body_part: "", date: todayISO(), time: "", price: "", sinal: "",
  payment_method: "pix", payment_status: "pago", paid_amount: "", notes: "",
  photos: [], pomada_purchased: false, pomada_price: "",
};

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [tattoos, setTattoos] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [tatOpen, setTatOpen] = useState(false);
  const [tatForm, setTatForm] = useState(tattooBlank);
  const [editingTattooId, setEditingTattooId] = useState(null);
  const [numOpen, setNumOpen] = useState(false);
  const [numValue, setNumValue] = useState("");
  const [numAuto, setNumAuto] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(null); // tattoo just created
  const [remOpen, setRemOpen] = useState(false);
  const [remForm, setRemForm] = useState({ type: "retoque", date: todayISO(), note: "", tattoo_id: "" });
  const [hasCarePdf, setHasCarePdf] = useState(false);
  const templates = useMessageTemplates();

  const load = async () => {
    try {
      const [c, t, n, r] = await Promise.all([
        getClient(id),
        listTattoos({ client_id: id }),
        listLuckyNumbers(id),
        listReminders({ client_id: id }),
      ]);
      setClient(c); setTattoos(t); setNumbers(n); setReminders(r);
    } catch {
      toast.error("Cliente não encontrada");
      nav("/clientes");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    getSettings().then((s) => setHasCarePdf(!!s.has_care_pdf)).catch(() => {});
  }, []);

  const openEditTattoo = (t) => {
    setEditingTattooId(t.id);
    setTatForm({
      description: t.description || "",
      body_part: t.body_part || "",
      date: t.date || todayISO(),
      time: t.time || "",
      price: t.price ?? "",
      sinal: t.sinal ?? "",
      payment_method: t.payment_method || "pix",
      payment_status: t.payment_status || "pago",
      paid_amount: t.paid_amount ?? "",
      notes: t.notes || "",
      photos: photosOf(t),
      pomada_purchased: !!t.pomada_purchased,
      pomada_price: t.pomada_price ?? "",
    });
    setTatOpen(true);
  };

  const sendCare = () => {
    if (!hasCarePdf) {
      toast.error("Faça upload do PDF de cuidados em Configurações primeiro");
      return;
    }
    const fn = (client?.name || "").split(" ")[0];
    const msg = composeCareMessage(fn, carePdfPublicUrl(), templates.care);
    window.open(waLink(client?.phone || "", msg), "_blank");
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > 4 * 1024 * 1024);
    if (tooBig) { toast.error("Algumas fotos passam de 4 MB"); return; }
    const b64s = await Promise.all(files.map(fileToBase64));
    setTatForm({ ...tatForm, photos: [...tatForm.photos, ...b64s] });
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    setTatForm({ ...tatForm, photos: tatForm.photos.filter((_, i) => i !== idx) });
  };

  const saveTattoo = async (e) => {
    e.preventDefault();
    if (!tatForm.description || !tatForm.price) {
      toast.error("Preencha descrição e valor");
      return;
    }
    const payload = {
      description: tatForm.description,
      body_part: tatForm.body_part || null,
      date: tatForm.date,
      time: tatForm.time || null,
      price: Number(tatForm.price),
      sinal: tatForm.sinal ? Number(tatForm.sinal) : null,
      payment_method: tatForm.payment_method,
      payment_status: tatForm.payment_status,
      paid_amount: tatForm.payment_status === "parcial" ? Number(tatForm.paid_amount || 0) : null,
      pomada_purchased: tatForm.pomada_purchased,
      pomada_price: tatForm.pomada_purchased && tatForm.pomada_price ? Number(tatForm.pomada_price) : null,
      notes: tatForm.notes || null,
      photos: tatForm.photos,
    };
    try {
      if (editingTattooId) {
        await updateTattoo(editingTattooId, payload);
        toast.success("Sessão atualizada");
        setEditingTattooId(null);
        setTatForm(tattooBlank);
        setTatOpen(false);
        load();
      } else {
        const created = await createTattoo({ client_id: id, ...payload });
        toast.success("Tatuagem registrada");
        setTatForm(tattooBlank);
        setTatOpen(false);
        setConfirmOpen(created);  // open WhatsApp confirmation modal
        load();
      }
    } catch (err) {
      console.error("save tattoo failed:", err, err?.response?.data);
      toast.error(`Erro ao salvar: ${err?.response?.data?.detail || err?.message || "desconhecido"}`);
    }
  };

  const saveNumber = async (e) => {
    e.preventDefault();
    try {
      await createLuckyNumber({
        client_id: id,
        number: numAuto ? null : Number(numValue),
      });
      toast.success(numAuto ? "Número atribuído automaticamente" : "Número atribuído");
      setNumValue(""); setNumOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atribuir número");
    }
  };

  const fetchNext = async () => {
    const r = await nextLuckyNumber();
    setNumValue(String(r.next));
  };

  const removeTattoo = async (tid) => {
    if (!(await confirmAction("Remover esta tatuagem?"))) return;
    await deleteTattoo(tid);
    toast.success("Removida");
    load();
  };

  const removeNumber = async (nid) => {
    if (!(await confirmAction("Remover este número?"))) return;
    await deleteLuckyNumber(nid);
    toast.success("Removido");
    load();
  };

  const removeClient = async () => {
    if (!(await confirmAction("Excluir cliente e todos os seus dados? Esta ação é permanente."))) return;
    await deleteClient(id);
    toast.success("Cliente removida");
    nav("/clientes");
  };

  const openEdit = () => {
    setEditForm({
      name: client.name, phone: client.phone || "", instagram: client.instagram || "",
      birthday: client.birthday || "", notes: client.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    await updateClient(id, editForm);
    toast.success("Atualizado");
    setEditOpen(false);
    load();
  };

  const saveReminder = async (e) => {
    e.preventDefault();
    await createReminder({
      client_id: id,
      tattoo_id: remForm.tattoo_id || null,
      type: remForm.type,
      date: remForm.date,
      note: remForm.note || null,
    });
    toast.success("Lembrete criado");
    setRemForm({ type: "retoque", date: todayISO(), note: "", tattoo_id: "" });
    setRemOpen(false);
    load();
  };

  const toggleReminder = async (r) => {
    await updateReminder(r.id, { done: !r.done });
    load();
  };

  const removeReminder = async (rid) => {
    if (!(await confirmAction("Remover lembrete?"))) return;
    await deleteReminder(rid);
    load();
  };

  const changeTattooStatus = async (tattooId, status) => {
    try {
      const payload = { payment_status: status };
      await updateTattoo(tattooId, payload);
      toast.success("Status atualizado");
      load();
    } catch (err) {
      console.error("status update failed:", err, err?.response?.data);
      toast.error(`Erro ao atualizar status: ${err?.response?.data?.detail || err?.message || "desconhecido"}`);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mensagem copiada");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (!client) return <p className="text-[#5C6658]">Carregando…</p>;

  const totalPomada = tattoos.reduce((acc, t) => acc + (t.pomada_purchased ? Number(t.pomada_price || 0) : 0), 0);
  const countPomada = tattoos.filter((t) => t.pomada_purchased).length;
  const firstName = (client.name || "").split(" ")[0];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Link to="/clientes" className="inline-flex items-center gap-2 text-sm text-[#5C6658] hover:text-[#2E332C] mb-6" data-testid="back-clients">
        <ArrowLeft className="w-4 h-4" /> Clientes
      </Link>

      <div className="elegant-card p-8 mb-8">
        <div className="flex flex-wrap justify-between gap-6 items-start">
          <div>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2E332C] font-light tracking-tight">{client.name}</h1>
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#5C6658]">
              {client.instagram && <span className="flex items-center gap-2"><Instagram className="w-4 h-4" /> @{client.instagram.replace(/^@/,"")}</span>}
              {client.phone && <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> {client.phone}</span>}
              {client.birthday && <span className="flex items-center gap-2"><Cake className="w-4 h-4" /> {formatDate(client.birthday)}</span>}
            </div>
            {client.notes && <p className="text-sm text-[#5C6658] mt-4 max-w-xl italic">"{client.notes}"</p>}
            <div className="flex flex-wrap gap-2 mt-5">
              {client.phone && (
                <Button asChild size="sm" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white" data-testid="client-wa">
                  <a href={waLink(client.phone, composeGeneralMessage(firstName, templates.general))} target="_blank" rel="noreferrer">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                  </a>
                </Button>
              )}
              {client.instagram && (
                <Button asChild size="sm" variant="outline" className="rounded-full border-[#E8E4D9]" data-testid="client-ig">
                  <a href={igDMLink(client.instagram)} target="_blank" rel="noreferrer">
                    <Instagram className="w-4 h-4 mr-1.5" /> Instagram
                  </a>
                </Button>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openEdit} className="rounded-full border-[#E8E4D9]" data-testid="edit-client">
              <Edit2 className="w-4 h-4 mr-1" /> Editar
            </Button>
            <Button variant="outline" onClick={removeClient} className="rounded-full border-[#B86B6B]/40 text-[#B86B6B] hover:bg-[#FCEAEA]" data-testid="delete-client">
              <Trash2 className="w-4 h-4 mr-1" /> Excluir
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mt-8 pt-6 border-t border-[#E8E4D9]">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Tatuagens</p>
            <p className="font-serif-display text-3xl text-[#2E332C]">{tattoos.length}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Números</p>
            <p className="font-serif-display text-3xl text-[#C4A77D]">{numbers.length}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Pomadas ({countPomada})</p>
            <p className="font-serif-display text-3xl text-[#7B8B6F]">{formatBRL(totalPomada)}</p>
          </div>
        </div>
      </div>

      {/* Lucky Numbers */}
      <div className="elegant-card p-8 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Números da sorte</h2>
            <p className="text-xs text-[#5C6658] mt-1 uppercase tracking-wider">{numbers.length} número(s)</p>
          </div>
          <Dialog open={numOpen} onOpenChange={(v) => { setNumOpen(v); if (v) fetchNext(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-number" className="rounded-full bg-[#C4A77D] hover:bg-[#B0905F] text-white"><Ticket className="w-4 h-4 mr-2" /> Atribuir número</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9]">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">Atribuir número da sorte</DialogTitle>
                <DialogDescription>Atribua um número manualmente ou use o próximo automático.</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveNumber} className="space-y-4 mt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={numAuto} onChange={(e) => setNumAuto(e.target.checked)} data-testid="number-auto-toggle" />
                  Próximo automático
                </label>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Número</Label>
                  <Input data-testid="number-input" type="number" min="1" disabled={numAuto} value={numValue} onChange={(e) => setNumValue(e.target.value)} className="mt-2 bg-white" />
                  <p className="text-xs text-[#5C6658] mt-2">Próximo disponível: {numValue || "—"}</p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="submit-number" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-8">Atribuir</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {numbers.length === 0 ? (
          <p className="text-sm text-[#5C6658]">Nenhum número atribuído ainda.</p>
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="numbers-list">
            {numbers.map((n) => (
              <div key={n.id} className="group relative flex items-center gap-3 px-5 py-3 rounded-full border border-[#C4A77D]/40 bg-[#F4E8D6]/40">
                <span className="font-serif-display text-2xl text-[#7B5E2E]">{n.number}</span>
                <button onClick={() => removeNumber(n.id)} className="opacity-0 group-hover:opacity-100 transition text-[#B86B6B]" data-testid={`remove-number-${n.number}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reminders */}
      <div className="elegant-card p-8 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#7B8B6F]" strokeWidth={1.5} />
            <div>
              <h2 className="font-serif-display text-2xl text-[#2E332C]">Lembretes & Retoques</h2>
              <p className="text-xs text-[#5C6658] mt-1 uppercase tracking-wider">{reminders.length} lembrete(s)</p>
            </div>
          </div>
          <Dialog open={remOpen} onOpenChange={setRemOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full border-[#E8E4D9]" data-testid="add-reminder"><Plus className="w-4 h-4 mr-2" /> Novo lembrete</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9]">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">Novo lembrete</DialogTitle>
                <DialogDescription>Para retoques, sessões de continuação ou outro acompanhamento.</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveReminder} className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Tipo</Label>
                    <Select value={remForm.type} onValueChange={(v) => setRemForm({ ...remForm, type: v })}>
                      <SelectTrigger className="mt-2 bg-white" data-testid="reminder-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retoque">Retoque</SelectItem>
                        <SelectItem value="sessao">Sessão</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Data</Label>
                    <Input type="date" value={remForm.date} onChange={(e) => setRemForm({ ...remForm, date: e.target.value })} className="mt-2 bg-white" data-testid="reminder-date" />
                  </div>
                </div>
                {tattoos.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Vincular à tatuagem (opcional)</Label>
                    <Select value={remForm.tattoo_id} onValueChange={(v) => setRemForm({ ...remForm, tattoo_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 bg-white"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {tattoos.map((t) => <SelectItem key={t.id} value={t.id}>{t.description} — {formatDate(t.date)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Observação</Label>
                  <Textarea value={remForm.note} onChange={(e) => setRemForm({ ...remForm, note: e.target.value })} className="mt-2 bg-white" rows={2} data-testid="reminder-note" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-8" data-testid="submit-reminder">Criar lembrete</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {reminders.length === 0 ? (
          <p className="text-sm text-[#5C6658]">Nenhum lembrete para esta cliente.</p>
        ) : (
          <ul className="divide-y divide-[#E8E4D9]" data-testid="client-reminders">
            {reminders.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-3">
                <input
                  type="checkbox"
                  checked={!!r.done}
                  onChange={() => toggleReminder(r)}
                  data-testid={`client-reminder-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${r.done ? "text-[#5C6658] line-through" : "text-[#2E332C]"}`}>
                    <span className="capitalize">{r.type}</span> · {formatDate(r.date)}
                  </p>
                  {r.note && <p className="text-xs text-[#5C6658] italic">"{r.note}"</p>}
                </div>
                <button onClick={() => removeReminder(r.id)} className="text-[#B86B6B] hover:text-[#8C4848]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tattoos */}
      <div className="elegant-card p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Histórico de tatuagens</h2>
            <p className="text-xs text-[#5C6658] mt-1 uppercase tracking-wider">{tattoos.length} sessão(ões)</p>
          </div>
          <Dialog open={tatOpen} onOpenChange={(v) => { setTatOpen(v); if (!v) { setEditingTattooId(null); setTatForm(tattooBlank); } else if (!editingTattooId) { setTatForm(tattooBlank); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-tattoo" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white"><Plus className="w-4 h-4 mr-2" /> Nova Sessão</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9] max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">{editingTattooId ? "Editar sessão" : "Nova sessão de tatuagem"}</DialogTitle>
                <DialogDescription>{editingTattooId ? "Atualize os dados desta sessão." : "Registre a sessão com fotos, valor e sinal pago."}</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveTattoo} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Descrição*</Label>
                  <Input data-testid="tattoo-desc" value={tatForm.description} onChange={(e) => setTatForm({ ...tatForm, description: e.target.value })} className="mt-2 bg-white" placeholder="Ex.: fine line floral" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Parte do corpo</Label>
                    <Input data-testid="tattoo-body" value={tatForm.body_part} onChange={(e) => setTatForm({ ...tatForm, body_part: e.target.value })} className="mt-2 bg-white" placeholder="Ex.: braço" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Data*</Label>
                    <Input data-testid="tattoo-date" type="date" value={tatForm.date} onChange={(e) => setTatForm({ ...tatForm, date: e.target.value })} className="mt-2 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Horário</Label>
                    <Input data-testid="tattoo-time" type="time" value={tatForm.time} onChange={(e) => setTatForm({ ...tatForm, time: e.target.value })} className="mt-2 bg-white" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Valor (R$)*</Label>
                    <Input data-testid="tattoo-price" type="number" step="0.01" value={tatForm.price} onChange={(e) => setTatForm({ ...tatForm, price: e.target.value })} className="mt-2 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Sinal (R$)</Label>
                    <Input data-testid="tattoo-sinal" type="number" step="0.01" value={tatForm.sinal} onChange={(e) => setTatForm({ ...tatForm, sinal: e.target.value })} className="mt-2 bg-white" placeholder="0,00" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Método</Label>
                    <Select value={tatForm.payment_method} onValueChange={(v) => setTatForm({ ...tatForm, payment_method: v })}>
                      <SelectTrigger className="mt-2 bg-white" data-testid="tattoo-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pix">Pix</SelectItem>
                        <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        <SelectItem value="cartao">Cartão</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Status</Label>
                    <Select value={tatForm.payment_status} onValueChange={(v) => setTatForm({ ...tatForm, payment_status: v })}>
                      <SelectTrigger className="mt-2 bg-white" data-testid="tattoo-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pago">Pago</SelectItem>
                        <SelectItem value="pendente">Pendente</SelectItem>
                        <SelectItem value="parcial">Parcial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {tatForm.payment_status === "parcial" && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Pago além do sinal (R$)</Label>
                      <Input data-testid="tattoo-paid" type="number" step="0.01" value={tatForm.paid_amount} onChange={(e) => setTatForm({ ...tatForm, paid_amount: e.target.value })} className="mt-2 bg-white" />
                    </div>
                  )}
                </div>

                {/* Pomada */}
                <div className="border border-[#E8E4D9] rounded-xl p-4 bg-[#F5F2E8]/40">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tatForm.pomada_purchased}
                      onChange={(e) => setTatForm({ ...tatForm, pomada_purchased: e.target.checked })}
                      data-testid="tattoo-pomada"
                    />
                    <span className="font-medium text-[#2E332C]">Comprou a pomada</span>
                  </label>
                  {tatForm.pomada_purchased && (
                    <div className="mt-3">
                      <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Valor da pomada (R$)</Label>
                      <Input type="number" step="0.01" value={tatForm.pomada_price} onChange={(e) => setTatForm({ ...tatForm, pomada_price: e.target.value })} className="mt-2 bg-white max-w-[180px]" data-testid="tattoo-pomada-price" />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Fotos da tatuagem (múltiplas)</Label>
                  <Input data-testid="tattoo-photos" type="file" accept="image/*" multiple onChange={addPhotos} className="mt-2 bg-white" />
                  {tatForm.photos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {tatForm.photos.map((p, i) => (
                        <div key={i} className="relative group">
                          <img src={p} alt="" className="w-full h-20 object-cover rounded-lg border border-[#E8E4D9]" />
                          <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#B86B6B] text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Observações</Label>
                  <Textarea data-testid="tattoo-notes" value={tatForm.notes} onChange={(e) => setTatForm({ ...tatForm, notes: e.target.value })} className="mt-2 bg-white" rows={2} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="submit-tattoo" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-8">{editingTattooId ? "Atualizar sessão" : "Salvar sessão"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {tattoos.length === 0 ? (
          <p className="text-sm text-[#5C6658]">Nenhuma tatuagem registrada.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="tattoos-list">
            {tattoos.map((t) => {
              const photos = photosOf(t);
              const balance = balanceOfTattoo(t);
              return (
                <div key={t.id} className="border border-[#E8E4D9] rounded-2xl p-5 bg-[#FDFCF7]">
                  <div className="flex gap-4">
                    {photos[0] ? (
                      <div className="relative shrink-0">
                        <img src={photos[0]} alt="" className="w-24 h-24 object-cover rounded-xl border border-[#E8E4D9]" />
                        {photos.length > 1 && (
                          <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">+{photos.length - 1}</span>
                        )}
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-[#F1EEE3] flex items-center justify-center text-[#7B8B6F] shrink-0">✦</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif-display text-lg text-[#2E332C] leading-tight">{t.description}</h3>
                      {t.body_part && <p className="text-xs text-[#5C6658] mt-1">{t.body_part}</p>}
                      <p className="text-xs text-[#5C6658] mt-2">{formatDate(t.date)}{t.time ? ` · ${t.time}` : ""} · {paymentLabel(t.payment_method)}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="font-serif-display text-xl text-[#2E332C]">{formatBRL(t.price)}</span>
                        <Select value={t.payment_status} onValueChange={(v) => changeTattooStatus(t.id, v)}>
                          <SelectTrigger
                            data-testid={`tattoo-status-select-${t.id}`}
                            className={`h-7 w-auto gap-1 px-3 text-[10px] uppercase tracking-wider rounded-full border ${statusBadge(t.payment_status)}`}
                          >
                            <SelectValue>{statusLabel(t.payment_status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pago">Pago</SelectItem>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="parcial">Parcial</SelectItem>
                          </SelectContent>
                        </Select>
                        {t.pomada_purchased && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-[#C4A77D]/40 bg-[#F4E8D6]/60 text-[#7B5E2E]">Pomada {t.pomada_price ? formatBRL(t.pomada_price) : ""}</span>
                        )}
                      </div>
                      {(t.sinal || balance > 0) && (
                        <div className="flex gap-3 mt-2 text-xs text-[#5C6658]">
                          {t.sinal ? <span>Sinal: <b className="text-[#7B8B6F]">{formatBRL(t.sinal)}</b></span> : null}
                          {balance > 0 && <span>Saldo: <b className="text-[#B86B6B]">{formatBRL(balance)}</b></span>}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {client.phone && (
                        <a
                          href={waLink(client.phone, composeAppointmentMessage(firstName, t, templates.appointment))}
                          target="_blank" rel="noreferrer"
                          className="w-8 h-8 rounded-full bg-[#E5EBE0] hover:bg-[#D6E0CB] flex items-center justify-center"
                          title="Enviar dados via WhatsApp"
                          data-testid={`tattoo-wa-${t.id}`}
                        >
                          <Send className="w-3.5 h-3.5 text-[#4F5E45]" />
                        </a>
                      )}
                      <button
                        onClick={sendCare}
                        className="w-8 h-8 rounded-full bg-[#F4E8D6] hover:bg-[#E8D8B8] text-[#7B5E2E] flex items-center justify-center"
                        title="Enviar PDF de cuidados"
                        data-testid={`tattoo-care-${t.id}`}
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => openEditTattoo(t)}
                        className="w-8 h-8 rounded-full hover:bg-[#F1EEE3] text-[#7B8B6F] flex items-center justify-center"
                        title="Editar sessão"
                        data-testid={`edit-tattoo-${t.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeTattoo(t.id)} className="w-8 h-8 rounded-full hover:bg-[#FCEAEA] text-[#B86B6B] flex items-center justify-center" data-testid={`remove-tattoo-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {t.notes && <p className="text-xs text-[#5C6658] italic mt-3 pl-2 border-l-2 border-[#E8E4D9]">"{t.notes}"</p>}
                  {photos.length > 1 && (
                    <div className="grid grid-cols-5 gap-2 mt-4">
                      {photos.slice(1, 6).map((p, i) => (
                        <img key={i} src={p} alt="" className="w-full h-14 object-cover rounded-md border border-[#E8E4D9]" />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation modal after creating tattoo */}
      <Dialog open={!!confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(null)}>
        <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Enviar agendamento?</DialogTitle>
            <DialogDescription>
              {client.phone
                ? "Enviar os dados da sessão para a cliente via WhatsApp/Instagram."
                : "Sem telefone cadastrado — abrirá o WhatsApp em branco para você escolher a conversa, ou copie a mensagem abaixo."}
            </DialogDescription>
          </DialogHeader>
          {confirmOpen && (
            <div className="space-y-4">
              <div className="bg-[#F5F2E8] rounded-xl p-4 text-xs text-[#5C6658] whitespace-pre-line italic max-h-60 overflow-y-auto">
                {composeAppointmentMessage(firstName, confirmOpen)}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  asChild
                  className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white flex-1"
                  data-testid="confirm-send-wa"
                >
                  <a
                    href={waLink(client.phone || "", composeAppointmentMessage(firstName, confirmOpen, templates.appointment))}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setConfirmOpen(null)}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Enviar no WhatsApp
                  </a>
                </Button>
                {client.instagram && (
                  <Button asChild variant="outline" className="rounded-full border-[#E8E4D9]" data-testid="confirm-send-ig">
                    <a href={igDMLink(client.instagram)} target="_blank" rel="noreferrer" onClick={() => setConfirmOpen(null)}>
                      <Instagram className="w-4 h-4 mr-2" /> Instagram
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(composeAppointmentMessage(firstName, confirmOpen, templates.appointment))}
                  className="rounded-full border-[#E8E4D9]"
                  data-testid="confirm-copy"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copiar
                </Button>
                <Button variant="ghost" onClick={() => setConfirmOpen(null)} className="rounded-full text-[#5C6658]" data-testid="confirm-skip">
                  Pular
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Editar cliente</DialogTitle>
            <DialogDescription>Atualize os dados cadastrais.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3 mt-2">
            <div><Label className="text-xs uppercase tracking-wider text-[#5C6658]">Nome</Label>
              <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-2 bg-white" data-testid="edit-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#5C6658]">Telefone</Label>
                <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-2 bg-white" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#5C6658]">Instagram</Label>
                <Input value={editForm.instagram || ""} onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })} className="mt-2 bg-white" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-[#5C6658]">Aniversário</Label>
              <Input type="date" value={editForm.birthday || ""} onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })} className="mt-2 bg-white" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#5C6658]">Notas</Label>
              <Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-2 bg-white" rows={3} /></div>
            <DialogFooter>
              <Button type="submit" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-8" data-testid="submit-edit-client">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
